import React from 'react';
import { motion } from 'framer-motion';
import { BookOpen } from 'lucide-react';
const tomos = [{
  number: 'TOMO 1',
  title: 'Las Bases del Taller Profesional',
  color: 'bg-green',
  textColor: 'text-green',
  problem: 'El primer contacto de un alumno con el procesamiento de alimentos puede ser transformador o confuso. Este tomo establece los fundamentos correctos desde el primer día.',
  bullets: ['Introducción a la industrialización de alimentos', 'Organización y seguridad del taller', 'Higiene y Buenas Prácticas de Manufactura', 'Controles básicos: temperatura, pH y actividad de agua', 'Limpieza y desinfección en el taller escolar'],
  result: 'El alumno comprende cómo funciona un taller alimentario seguro y organizado — antes de tocar un ingrediente.'
}, {
  number: 'TOMO 2',
  title: 'De la Materia Prima a la Conservación',
  color: 'bg-olive',
  textColor: 'text-olive',
  problem: 'Los alumnos aprenden procesos pero no entienden por qué los alimentos se conservan ni cuándo son seguros. Este tomo conecta la ciencia con la práctica.',
  bullets: ['Selección, recepción y evaluación de materia prima', 'Deterioro microbiano y cómo prevenirlo', 'Métodos de conservación: calor, frío, sal, azúcar, acidez', 'El pH como parámetro de seguridad alimentaria', 'Almacenamiento correcto según el tipo de producto'],
  result: 'El alumno comprende cómo la materia prima y las condiciones del proceso afectan directamente el producto final.'
}, {
  number: 'TOMO 3',
  title: 'Tecnología de Lácteos y Cárnicos',
  color: 'bg-terracota',
  textColor: 'text-terracota',
  problem: 'Lácteos y cárnicos son los contenidos de mayor complejidad técnica. Sin el material correcto, se enseñan como recetas en lugar de como procesos técnicos.',
  bullets: ['Tecnología de lácteos: leche, yogur, quesos, mantequilla, dulce de leche', 'Tecnología de cárnicos: embutidos, curados, fermentados', 'Formulaciones con porcentajes exactos', 'Ingredientes funcionales: sal, nitritos, fosfatos', 'Puntos críticos de inocuidad en cada proceso'],
  result: 'El alumno conecta ingredientes, procedimientos, controles y resultados. Aprende el proceso, no solo el producto.'
}, {
  number: 'TOMO 4',
  title: 'Del Envase al Consumidor',
  color: 'bg-petrol',
  textColor: 'text-petrol',
  problem: 'Muchos docentes terminan el proceso en la elaboración. Este tomo enseña que el proceso continúa hasta que el producto llega al consumidor.',
  bullets: ['Tipos de envase y criterios de selección', 'Técnicas de envasado y cierre', 'Rotulado: requisitos legales generales para América Latina', 'Almacenamiento y conservación del producto terminado', 'Canales de comercialización y comunicación de calidad'],
  result: 'El alumno comprende que el proceso no termina en la olla. Aprende a presentar y comercializar lo que produce.'
}, {
  number: 'TOMO 5',
  title: 'Del Taller al Emprendimiento',
  color: 'bg-[#8B4513]',
  textColor: 'text-[#8B4513]',
  problem: 'Los alumnos terminan el año sabiendo elaborar pero sin saber cuánto cuesta ni cómo vender. Este tomo cierra el ciclo.',
  bullets: ['Cálculo de costos de producción con precios reales', 'Diferencia entre precio de costo y precio de valor percibido', 'Análisis económico por producto elaborado', 'Proyecto Final Productivo 360°: producto, envase, cálculo', 'Acceso a mercados: primeros pasos para vender legalmente'],
  result: 'El alumno desarrolla un proyecto productivo real — con producto elaborado, calculado, envasado y listo para presentar.'
}];
export function TomosSection() {
  return <section className="py-24 px-4 bg-cream" data-id="element-386" data-mp="12sx4hj9k2ja6">
      <div className="max-w-5xl mx-auto" data-id="element-387" data-mp="12s59a9jvenkf">
        <div className="text-center mb-16" data-id="element-388" data-mp="38gxl8a8ci045">
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-green mb-4" data-id="element-389" data-mp="38i8x6xby2e6c">
            El sistema completo en 5 tomos
          </h2>
          <p className="text-xl text-slate" data-id="element-390" data-mp="3dhv9n4jy7qe3">
            Un recorrido ordenado desde los fundamentos hasta el emprendimiento
          </p>
        </div>

        <div className="space-y-8" data-id="element-391" data-mp="3mhi2h1ym3nvj">
          {tomos.map((tomo, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-white rounded-2xl border border-gold/30 overflow-hidden shadow-sm flex flex-col md:flex-row" data-id="element-392" data-mp="3rgl8aomf1y4k" data-mpi={index}>
              <div className={`${tomo.color} p-6 md:w-1/3 flex flex-col justify-center text-white relative overflow-hidden`} data-id="element-393" data-mp="e2q6di76sb8v">
                <div className="absolute -right-4 -bottom-4 opacity-10" data-id="element-394" data-mp="t46sonlfgx4b">
                  <BookOpen size={120} data-id="element-395" data-mp="o53ot6h6owxx" />
                </div>
                <span className="font-bold tracking-widest text-sm mb-2 opacity-80" data-id="element-396" data-mp="o3iu9guvde02">
                  {tomo.number}
                </span>
                <h3 className="text-2xl font-serif font-bold leading-tight relative z-10" data-id="element-397" data-mp="f4fa09jyusjz">
                  {tomo.title}
                </h3>
              </div>

              <div className="p-6 md:p-8 md:w-2/3" data-id="element-398" data-mp="1bwiun9865wdx">
                <div className="mb-6" data-id="element-399" data-mp="16v1whlh2ko11">
                  <h4 className={`text-sm font-bold uppercase tracking-wider mb-2 ${tomo.textColor}`} data-id="element-400" data-mp="16w3hahnw0ovy">
                    ¿Qué problema resuelve?
                  </h4>
                  <p className="text-ink text-sm leading-relaxed" data-id="element-401" data-mp="y0x8d9tblqp7">
                    {tomo.problem}
                  </p>
                </div>

                <div className="mb-6" data-id="element-402" data-mp="1ptxnknxmsman">
                  <h4 className="text-sm font-bold text-slate uppercase tracking-wider mb-3" data-id="element-403" data-mp="1pswbcg4vptrc">
                    Contenidos principales:
                  </h4>
                  <ul className="space-y-2" data-id="element-404" data-mp="3p1e5k3e1y0jp">
                    {tomo.bullets.map((bullet, i) => <li key={i} className="flex items-start gap-2 text-sm text-ink" data-id="element-405" data-mp="3aczzzkp93xzg" data-mpi={i}>
                        <span className={tomo.textColor} data-id="element-406" data-mp="3f8yp5aielk0x">•</span>
                        <span data-id="element-407" data-mp="319yyxyf8sx2n">{bullet}</span>
                      </li>)}
                  </ul>
                </div>

                <div className={`p-4 rounded-xl bg-cream-light border border-cream-dark`} data-id="element-408" data-mp="2rddsd8nkrdwp">
                  <span className={`font-bold text-sm block mb-1 ${tomo.textColor}`} data-id="element-409" data-mp="2wbnnjj9aueib">
                    Resultado en el aula:
                  </span>
                  <p className="text-ink text-sm italic" data-id="element-410" data-mp="ully3ra532t7">{tomo.result}</p>
                </div>
              </div>
            </motion.div>)}
        </div>
        <p className="text-center text-sm text-slate mt-8 italic" data-id="element-411" data-mp="2f11uhlp5evyb">
          Las normativas específicas varían por país. El material presenta
          principios generales adaptables.
        </p>
      </div>
    </section>;
}